import {
  Button,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
  toast,
} from "@devhop/ui";
import { CheckCircle, Eye, Pencil } from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { PromotionRecordListItem } from "@/modules/hr/promotion/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { useApprovePromotionRecordMutation } from "../api/queries";

type Props = {
  data: PromotionRecordListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onView: (row: PromotionRecordListItem) => void;
  onEdit?: (row: PromotionRecordListItem) => void;
};

function salary(v: number | null): string {
  if (v == null) return "-";
  return new Intl.NumberFormat("en-US").format(v);
}

export function PromotionRecordsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
  onView,
  onEdit,
}: Props) {
  const { has } = usePermissions();
  const canApprove = has(Permissions.hr_promotion.approve);
  const canUpdate = has(Permissions.hr_promotion.record_update);
  const approveMutation = useApprovePromotionRecordMutation();

  const handleApprove = async (row: PromotionRecordListItem) => {
    const ok = await confirm({
      title: "ຢືນຢັນການອະນຸມັດ",
      description: `ຢືນຢັນອະນຸມັດການເລື່ອນຂັ້ນຂອງ ${row.employeeName} ບໍ?`,
      actionText: "ອະນຸມັດ",
    });
    if (!ok) return;
    approveMutation.mutate(row.id, {
      onSuccess: () => toast.success("ອະນຸມັດບັນທຶກການເຕີບໂຕແລ້ວ"),
      onError: (err) => toast.error(err?.message ?? "ອະນຸມັດບໍ່ສຳເລັດ"),
    });
  };

  const columns: TanstackReactTable.ColumnDef<PromotionRecordListItem>[] = [
    createSortableColumn<PromotionRecordListItem>("employeeName", "ພະນັກງານ", {
      size: 180,
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.employeeName}</div>
          <div className="text-muted-foreground text-xs">
            {row.original.employeeEmail}
          </div>
        </div>
      ),
    }),
    createSortableColumn<PromotionRecordListItem>(
      "toPositionTitle",
      "ຕຳແໜ່ງ (ຈາກ → ໄປ)",
      {
        size: 220,
        cell: ({ row }) =>
          `${row.original.fromPositionTitle ?? "-"} → ${row.original.toPositionTitle}`,
      },
    ),
    createSortableColumn<PromotionRecordListItem>(
      "toLevelName",
      "ລະດັບ (ຈາກ → ໄປ)",
      {
        size: 200,
        cell: ({ row }) =>
          `${row.original.fromLevelName ?? "-"} → ${row.original.toLevelName ?? "-"}`,
      },
    ),
    createSortableColumn<PromotionRecordListItem>(
      "newSalary",
      "ເງິນເດືອນ (ເກົ່າ → ໃໝ່)",
      {
        size: 210,
        cell: ({ row }) =>
          `${salary(row.original.previousSalary)} → ${salary(row.original.newSalary)}`,
      },
    ),
    createSortableColumn<PromotionRecordListItem>("promotedAt", "ເລື່ອນຂັ້ນວັນທີ", {
      size: 150,
      cell: ({ row }) =>
        new Date(row.original.promotedAt).toLocaleDateString("en-GB"),
    }),
    createSortableColumn<PromotionRecordListItem>("approvedByName", "ຜູ້ອະນຸມັດ", {
      size: 140,
      cell: ({ row }) => row.original.approvedByName ?? "-",
    }),
    {
      id: "actions",
      header: "",
      size: 120,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {canApprove && row.original.approvedBy == null && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleApprove(row.original)}
              disabled={approveMutation.isPending}
              title="ອະນຸມັດ"
              aria-label="ອະນຸມັດ"
            >
              <CheckCircle className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            </Button>
          )}
          {canUpdate && onEdit && row.original.approvedBy == null && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(row.original)}
              title="ແກ້ໄຂ"
              aria-label="ແກ້ໄຂ"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onView(row.original)}
            title="ລາຍລະອຽດ"
            aria-label="ລາຍລະອຽດ"
          >
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <DataTable<PromotionRecordListItem, unknown>
        noDataMessage="ບໍ່ພົບບັນທຶກການເລື່ອນຂັ້ນ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          onSortingChange(sorting[0]?.id ?? "promotedAt", !!sorting[0]?.desc);
        }}
      />
  );
}
